<!--
  Stammdaten, Passwort und der Weg zum Akten-Safe.

  Änderbar ist nur, was der Kunde selbst am besten weiß: Telefon und Anschrift.
  Name und Kundennummer stehen fest — dafür ruft man an, und dann prüft ein
  Mensch, ob die Änderung stimmt.
-->
<script lang="ts">
  import { api } from '../lib/api.ts';
  import HilfeLink from '../lib/HilfeLink.svelte';
  import { sitzung, datum } from '../lib/sitzung.svelte.ts';
  import { passwortBeanstandung, portalBeweis } from '../lib/zugang.ts';

  /** Argon2id dauert spürbar — solange darf der Knopf nicht erneut feuern. */
  let passwortLaeuft = $state(false);

  interface AktenschrankInfo {
    paket: { status: string; sperrgrund: string | null; lesefrist_bis: string | null } | null;
    anfragen: {
      id: string;
      grund: string;
      dauer_stunden: number;
      status: string;
      angefragt_am: string;
      ticket_nummer: string | null;
    }[];
    adresse: string | null;
    hinweis: string;
  }

  let formular = $state({
    telefon: sitzung.kunde?.telefon ?? '',
    mobil: sitzung.kunde?.mobil ?? '',
    strasse: sitzung.kunde?.strasse ?? '',
    plz: sitzung.kunde?.plz ?? '',
    ort: sitzung.kunde?.ort ?? '',
  });
  let passwoerter = $state({ alt: '', neu: '', wiederholung: '' });
  let aktenschrank = $state<AktenschrankInfo | null>(null);
  let fehler = $state<string | null>(null);
  let hinweis = $state<string | null>(null);

  let zeigeLoeschenDialog = $state(false);
  let loeschenBestaetigung = $state('');
  let loeschenSendet = $state(false);
  let loeschenFehler = $state<string | null>(null);
  let loeschenAb = $state<string | null>(null);

  const hatVaultOderDas = $derived(sitzung.pakete.some((p) => p.typ === 'vault' || p.typ === 'das'));

  async function kontoLoeschen(): Promise<void> {
    loeschenSendet = true;
    loeschenFehler = null;
    try {
      const antwort = await api.post<{ loeschen_ab: string }>('/api/portal/konto/loeschen');
      loeschenAb = antwort.loeschen_ab;
    } catch (f) {
      loeschenFehler = f instanceof Error ? f.message : 'Das hat gerade nicht geklappt';
    } finally {
      loeschenSendet = false;
    }
  }

  $effect(() => {
    void (async () => {
      try {
        aktenschrank = await api.get<AktenschrankInfo>('/api/portal/aktenschrank');
      } catch {
        /* Ohne Akten-Safe-Paket bleibt der Bereich einfach leer. */
      }
    })();
  });

  async function speichern(ereignis: SubmitEvent): Promise<void> {
    ereignis.preventDefault();
    try {
      await api.patch('/api/portal/stammdaten', formular);
      hinweis = 'Gespeichert. Danke!';
      await sitzung.laden();
    } catch (f) {
      fehler = f instanceof Error ? f.message : 'Das hat gerade nicht geklappt';
    }
  }

  async function passwortAendern(ereignis: SubmitEvent): Promise<void> {
    ereignis.preventDefault();
    if (passwoerter.neu !== passwoerter.wiederholung) {
      fehler = 'Die beiden neuen Passwörter stimmen nicht überein.';
      return;
    }
    const beanstandung = passwortBeanstandung(passwoerter.neu);
    if (beanstandung) {
      fehler = beanstandung;
      return;
    }
    const adresse = sitzung.konto?.email;
    if (!adresse) {
      fehler = 'Bitte melden Sie sich neu an.';
      return;
    }
    passwortLaeuft = true;
    try {
      // Beide Passwörter bleiben hier; an den Server gehen nur die Beweise.
      // Das alte gehört zur bisherigen Adresse, das neue ebenso — die Adresse
      // ändert sich beim Passwortwechsel nicht.
      const [alt, neu] = await Promise.all([
        portalBeweis(adresse, passwoerter.alt),
        portalBeweis(adresse, passwoerter.neu),
      ]);
      await api.post('/api/portal/passwort', { alt, neu });
      hinweis = 'Ihr Passwort ist geändert. An anderen Geräten müssen Sie sich neu anmelden.';
      passwoerter = { alt: '', neu: '', wiederholung: '' };
      fehler = null;
    } catch (f) {
      fehler = f instanceof Error ? f.message : 'Das hat gerade nicht geklappt';
    } finally {
      passwortLaeuft = false;
    }
  }
</script>

<h1>Meine Daten</h1>
<HilfeLink slug="kundenbereich-daten-passwort" text="Anleitung: Meine Daten und Passwort ändern" />

{#if hinweis}<p class="erfolg">{hinweis}</p>{/if}
{#if fehler}<p class="fehler">{fehler}</p>{/if}

<div class="karte">
  <h2>Sie bei uns</h2>
  <p class="leise">
    {sitzung.kunde?.anzeigename} · Kundennummer {sitzung.kunde?.kd_nummer}
  </p>
  <p class="leise klein">
    Name und Kundennummer ändern wir für Sie — rufen Sie uns dafür einfach an.
  </p>

  <form onsubmit={speichern}>
    <label for="telefon">Telefon</label>
    <input id="telefon" type="tel" bind:value={formular.telefon} />

    <label for="mobil">Mobil</label>
    <input id="mobil" type="tel" bind:value={formular.mobil} />

    <label for="strasse">Straße und Hausnummer</label>
    <input id="strasse" type="text" bind:value={formular.strasse} />

    <label for="plz">Postleitzahl</label>
    <input id="plz" type="text" bind:value={formular.plz} />

    <label for="ort">Ort</label>
    <input id="ort" type="text" bind:value={formular.ort} />

    <div class="knopfreihe">
      <button type="submit">Speichern</button>
    </div>
  </form>
</div>

{#if aktenschrank?.paket}
  <div class="karte">
    <h2>Ihr Akten-Safe</h2>
    <p class="wichtig">{aktenschrank.hinweis}</p>

    {#if aktenschrank.paket.status === 'gesperrt'}
      <p class="klein">
        Das Paket ist derzeit pausiert{aktenschrank.paket.sperrgrund
          ? `: ${aktenschrank.paket.sperrgrund}`
          : ''}. <strong>Ihre Dokumente bleiben erhalten</strong>, Lesen und Herunterladen sind
        {#if aktenschrank.paket.lesefrist_bis}bis zum {datum(aktenschrank.paket.lesefrist_bis)}{/if}
        weiterhin möglich.
      </p>
    {/if}

    {#if aktenschrank.anfragen.length > 0}
      <h3>Wann wir um Einsicht gebeten haben</h3>
      <ul class="eintraege">
        {#each aktenschrank.anfragen as anfrage (anfrage.id)}
          <li>
            <div class="zeile">
              <span>{anfrage.grund}</span>
              <span class="marke">{anfrage.status === 'offen' ? 'wartet auf Sie' : anfrage.status}</span>
            </div>
            <div class="leise klein">
              {datum(anfrage.angefragt_am)}
              {#if anfrage.ticket_nummer} · zu Anfrage {anfrage.ticket_nummer}{/if}
              · für {anfrage.dauer_stunden} Stunden
            </div>
          </li>
        {/each}
      </ul>
      <p class="leise klein">
        Freigeben oder widerrufen können Sie im Akten-Safe selbst — dort ist Ihr
        Zugriffspasswort, das wir nicht kennen.
      </p>
    {/if}

    {#if aktenschrank.adresse}
      <div class="knopfreihe">
        <a class="knopfartig" href={aktenschrank.adresse} target="_blank" rel="noreferrer">
          Zum Akten-Safe
        </a>
      </div>
    {/if}
  </div>
{/if}

<div class="karte">
  <h2>Passwort ändern</h2>
  {#if aktenschrank?.paket}
    <!--
      Nur zeigen, wer einen Akten-Safe hat. Für alle anderen wäre es eine
      Warnung vor einer Folge, die sie gar nicht treffen kann.
    -->
    <p class="wichtig">
      Sie haben einen Akten-Safe. Wenn dort dasselbe Passwort gilt, geht er mit einem Klick
      auf. Ändern Sie es hier, fragt der Akten-Safe Sie wieder eigens danach — und zwar nach
      Ihrem <strong>bisherigen</strong> Passwort, das dort weiterhin gilt. Wenn Sie das nicht
      möchten, ändern Sie es anschließend auch im Akten-Safe.
    </p>
  {/if}
  <form onsubmit={passwortAendern}>
    <label for="alt">Bisheriges Passwort</label>
    <input id="alt" type="password" autocomplete="current-password" bind:value={passwoerter.alt} />

    <label for="neu">Neues Passwort</label>
    <input id="neu" type="password" autocomplete="new-password" bind:value={passwoerter.neu} />

    <label for="wdh">Neues Passwort wiederholen</label>
    <input id="wdh" type="password" autocomplete="new-password" bind:value={passwoerter.wiederholung} />

    <div class="knopfreihe">
      <button
        type="submit"
        disabled={passwortLaeuft || !passwoerter.alt || passwoerter.neu.length < 10}
      >
        {passwortLaeuft ? 'Einen Moment …' : 'Passwort ändern'}
      </button>
    </div>
  </form>
</div>

<div class="karte gefahrenzone">
  <h2>Konto löschen</h2>

  {#if loeschenAb}
    <p class="erfolg">
      Ihr Konto wird gelöscht. Wir haben Ihnen eine E-Mail mit einem Link geschickt, über den Sie
      das bis zum {datum(loeschenAb)} rückgängig machen können. Danach ist die Löschung
      endgültig.
    </p>
    <div class="knopfreihe">
      <button type="button" onclick={() => sitzung.abmelden()}>Zur Anmeldung</button>
    </div>
  {:else if zeigeLoeschenDialog}
    {#if loeschenFehler}<p class="fehler">{loeschenFehler}</p>{/if}
    <p class="wichtig">
      Was mit der Löschung passiert:
    </p>
    <ul class="klein">
      <li>Ihr Zugang zum Kundenbereich wird <strong>sofort</strong> gesperrt.</li>
      <li>
        Laufende Buchungen (Einheitenpaket, Passwort-Safe, Akten-Safe, Geräte) werden
        sofort gekündigt.
      </li>
      <li>Sie erhalten eine E-Mail mit einem Link, um das 14 Tage lang rückgängig zu machen.</li>
      <li>
        Danach löschen wir alle Ihre Daten in unserem Verwaltungssystem unwiderruflich —
        <strong>auch wir können sie dann nicht mehr wiederherstellen.</strong>
      </li>
      <li>
        Rechnungen bleiben aus steuerlichen Gründen zehn Jahre bestehen, aber ohne Ihren Namen.
      </li>
    </ul>
    {#if hatVaultOderDas}
      <p class="fehler">
        Sie haben Passwort-Safe und/oder Akten-Safe gebucht: Sichern Sie sich wichtige
        Daten von dort vorher selbst — nach der Löschung können wir Ihnen nicht mehr helfen,
        daran zu kommen.
      </p>
    {/if}

    <label for="loeschen-bestaetigung">Zur Bestätigung „LÖSCHEN" eingeben</label>
    <input id="loeschen-bestaetigung" type="text" bind:value={loeschenBestaetigung} />

    <div class="knopfreihe">
      <button
        type="button"
        class="gefahr"
        disabled={loeschenBestaetigung !== 'LÖSCHEN' || loeschenSendet}
        onclick={kontoLoeschen}
      >
        Konto endgültig löschen
      </button>
      <button
        type="button"
        class="sekundaer"
        onclick={() => {
          zeigeLoeschenDialog = false;
          loeschenBestaetigung = '';
          loeschenFehler = null;
        }}
      >
        Abbrechen
      </button>
    </div>
  {:else}
    <p class="leise klein">
      Sie können Ihr Konto und alle bei uns gespeicherten Daten jederzeit selbst löschen lassen.
    </p>
    <div class="knopfreihe">
      <button type="button" class="gefahr" onclick={() => (zeigeLoeschenDialog = true)}>
        Konto löschen
      </button>
    </div>
  {/if}
</div>

<style>
  .gefahrenzone { border-color: var(--rot-rand); }
  .gefahrenzone h2 { color: var(--warnung); margin-top: 0; }
  .knopfartig {
    display: inline-flex;
    align-items: center;
    min-height: 56px;
    padding: 0.7rem 1.75rem;
    border: 2px solid var(--rand);
    border-radius: var(--radius);
    background: var(--flaeche);
    color: var(--tb-blau);
    font-weight: 700;
    text-decoration: none;
  }

  .knopfartig:hover { border-color: var(--tb-blau); background: var(--hintergrund); }
</style>
